import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, of, take, tap } from 'rxjs';
import { APIURL } from '../shared/consts/url.const';
import {
	CaseStudyResponseCreation,
	ChatDataResponse,
	FormulaResponseCreation,
	SummaryResponseCreation,
	TermResponseCreation,
	CreationId,
	ChatList,
	ChatPost,
	AllowedActionsResponse
} from '../models/api/chats.model';

@Injectable({
	providedIn: 'root'
})
export class ChatService {
	apiUrl = APIURL;

	httpClient = inject(HttpClient);

	oauthService = inject(OAuthService);

	personalityId = signal(0);

	getChats(subjectId?: number, topicIDs?: number[]) {
		let params = new HttpParams();
		if (subjectId) {
			params = params.set('SubjectId', subjectId);
		}
		if (topicIDs?.length) {
			topicIDs.forEach((id) => {
				params = params.append('TopicIds', id);
			});
		}
		return this.httpClient.get<ChatList>(`${this.apiUrl}/chats`, { params });
	}

	getChatById(id: number, page = 1, pageSize = 10) {
		const params = new HttpParams().set('page', page.toString()).set('pageSize', pageSize.toString());
		return this.httpClient.get<ChatDataResponse>(`${this.apiUrl}/chats/${id}`, { params });
	}

	addChat(chat: ChatPost) {
		return this.httpClient.post(`${this.apiUrl}/chats`, { chat });
	}

	createChat(name: string, topics: number[]) {
		return this.httpClient.post<CreationId>(`${this.apiUrl}/chats`, {
			name,
			topics,
			personalityId: this.personalityId()
		});
	}

	removeChat(chatId: number) {
		return this.httpClient.delete(`${this.apiUrl}/chats/${chatId}`);
	}

	renameChat(chatId: number, name: string) {
		const headers = { 'Content-Type': 'application/json' };
		return this.httpClient.patch(`${this.apiUrl}/chats/${chatId}`, { name: name }, { headers: headers });
	}

	createTest(chatId: number, numberOfQuestions: number) {
		return this.httpClient.post<CreationId>(`${this.apiUrl}/chats/${chatId}/test`, {
			questionAmount: numberOfQuestions
		});
	}

	createSet(
		chatId: number,
		types: Array<'tests' | 'freeQuestions' | 'truefalses' | 'flashcards'>,
		topicIds: number[],
		maxAmount: number | null = null
	) {
		const type = {
			test: types.includes('tests'),
			freeQuestion: types.includes('freeQuestions'),
			trueFalse: types.includes('truefalses'),
			flashcard: types.includes('flashcards')
		};
		return this.httpClient.post<CreationId>(`${this.apiUrl}/chats/${chatId}/set`, {
			type: type,
			topicIds: topicIds,
			maxAmount: maxAmount
		});
	}

	createSetCase(chatId: number, caseId: number, topicIds: number[]) {
		return this.httpClient.post<CreationId>(`${this.apiUrl}/chats/${chatId}/setcase`, {
			caseId: caseId,
			topicIds: topicIds
		});
	}

	createChatActivity(chatId: number, weekActivityId: number) {
		return this.httpClient.post<CreationId>(`${this.apiUrl}/chats/${chatId}/activity`, { weekActivityId });
	}

	getTest(chatId: number) {
		return this.httpClient.get<CreationId>(`${this.apiUrl}/chats/${chatId}/tests`);
	}

	createSummary(chatId: number, topicIds: number[]) {
		let params = new HttpParams();
		topicIds.forEach((id) => {
			params = params.append('topicIds', id);
		});
		return this.httpClient.get<SummaryResponseCreation>(`${this.apiUrl}/chats/${chatId}/summaries`, { params });
	}

	createFormula(chatId: number, topicIds: number[]) {
		let params = new HttpParams();
		topicIds.forEach((id) => {
			params = params.append('topicIds', id);
		});
		return this.httpClient.get<FormulaResponseCreation>(`${this.apiUrl}/chats/${chatId}/formulas`, { params });
	}
	createTerm(chatId: number, topicIds: number[]) {
		let params = new HttpParams();
		topicIds.forEach((id) => {
			params = params.append('topicIds', id);
		});
		return this.httpClient.get<TermResponseCreation>(`${this.apiUrl}/chats/${chatId}/terms`, { params });
	}

	getAllowedActions(subjectId: number) {
		const cacheKey = 'allowedActions' + subjectId;

		const observable = this.httpClient
			.get<AllowedActionsResponse>(`${this.apiUrl}/subjects/${subjectId}/cards`)
			.pipe(tap((res) => sessionStorage.setItem(cacheKey, JSON.stringify(res))));

		return this.returnCacheObservable(cacheKey, observable);
	}

	createCaseStudy(chatId: number) {
		return this.httpClient.get<CaseStudyResponseCreation>(`${this.apiUrl}/chats/${chatId}/case-study`);
	}

	createFeedback(chatId: number, chatHistoryId: number, isGoodFeedback: boolean, comment: string | null = null) {
		const body = !comment ? { isGoodFeedback } : { isGoodFeedback, comment };
		return this.httpClient.post(`${this.apiUrl}/chats/${chatId}/history/${chatHistoryId}/feedback`, body);
	}

	uploadFile(file: File, guid: string) {
		const formData = new FormData();
		formData.append('file', file);
		return this.httpClient.put(`${this.apiUrl}/chats/file`, formData, {
			headers: {
				'X-Process-Id': guid
			}
		});
	}

	addFilestoChat(chatId: number, documentIds: number[]) {
		return this.httpClient.post(`${this.apiUrl}/chats/${chatId}/file`, documentIds);
	}

	deleteFile(documentId: number) {
		return this.httpClient.delete(`${this.apiUrl}/chats/file/${documentId}`);
	}

	cancelProcess(guid: string) {
		return this.httpClient.post(`${this.apiUrl}/chats/process/${guid}/cancel`, null);
	}

	private returnCacheObservable<T>(cacheKey: string, fallbackObservable: Observable<T>): Observable<T> {
		const cachedResponse = sessionStorage.getItem(cacheKey);

		if (cachedResponse) {
			try {
				return of(JSON.parse(cachedResponse)).pipe(take(1));
			} catch (error) {
				sessionStorage.removeItem(cacheKey);
			}
		}
		return fallbackObservable.pipe(tap((res) => sessionStorage.setItem(cacheKey, JSON.stringify(res))));
	}
}
